import { useParams, Navigate } from "react-router-dom";
import { PublicLayout } from "@/components/public/PublicLayout";

const DOCS = {
  privacy: {
    title: "Privacy Policy",
    updated: "October 2026",
    sections: [
      { h: "What we collect", p: "Recruiter accounts: name, work email, company, hashed password. Candidate interviews: audio, video, transcripts, code submissions, keystroke timings, and proctor signals. Billing: handled by Stripe; we never see full card numbers." },
      { h: "Why we collect it", p: "To run the interview product (schedule, record, evaluate, score), produce scorecards, and provide auditability. Candidate recordings exist for 90 days by default; recruiters can shorten this per workspace." },
      { h: "Who we share with", p: "Infrastructure sub-processors only (hosting, object storage, LLM providers, email). Full list on request. We never sell personal data." },
      { h: "Your rights", p: "Candidates can request a full export or deletion of their interview data by emailing privacy@recruitsift.com. Recruiters can bulk-export via the Candidate Detail page or /api/exports." },
      { h: "Security", p: "TLS in transit, encryption at rest (B2 S3-compat), role-based access, tenant-scoped reads/writes. SOC 2 Type II audit in-flight." },
    ],
  },
  terms: {
    title: "Terms of Service",
    updated: "October 2026",
    sections: [
      { h: "Acceptance", p: "By creating a RecruitSift workspace, you agree to these terms on behalf of your organisation. Breach of acceptable use (spam, harassment, discriminatory hiring) terminates access without refund." },
      { h: "Your content", p: "You own your job data, candidate contacts, and interview recordings. We license the right to process this content solely to deliver the service." },
      { h: "Billing", p: "Monthly subscriptions. Prepaid annual plans available. Refund windows: 14 days for Growth, net-30 for Enterprise. Overages billed on the next cycle." },
      { h: "SLA", p: "99.9% uptime for Growth; 99.95% for Enterprise with downtime credits. See status.recruitsift.com for live posture." },
      { h: "Liability", p: "Total liability capped at the trailing twelve months of fees. We're not liable for hiring decisions you make based on scorecards — RecruitSift is a decision support product, not a decision maker." },
    ],
  },
  cookies: {
    title: "Cookie Policy",
    updated: "October 2026",
    sections: [
      { h: "What are cookies", p: "Cookies are small files stored on your device. We use them to keep you signed in, remember workspace preferences, and anonymously measure product usage." },
      { h: "Categories we use", p: "Strictly necessary (session, CSRF). Preferences (theme, workspace). Analytics (page views, feature usage via a privacy-preserving provider). No ad-tracking cookies." },
      { h: "Managing cookies", p: "You can block non-essential cookies via your browser settings or our in-app Settings → Privacy panel. Strictly necessary cookies cannot be blocked without breaking sign-in." },
    ],
  },
  gdpr: {
    title: "GDPR",
    updated: "October 2026",
    sections: [
      { h: "Role under GDPR", p: "For candidate interview data, you (the recruiter workspace) are the Data Controller and RecruitSift is a Data Processor. We have a standard DPA ready — email dpo@recruitsift.com." },
      { h: "Lawful basis", p: "Processing is based on your legitimate interest in evaluating candidates for a role, with explicit candidate consent captured at the start of every interview." },
      { h: "Data residency", p: "Default EU workspaces are hosted on EU-region infrastructure. Enterprise customers can request specific residency." },
      { h: "DPIA support", p: "We provide a template DPIA and sub-processor list on request. Email dpo@recruitsift.com." },
    ],
  },
};

export default function LegalPage() {
  const { doc } = useParams();
  const data = DOCS[doc];
  if (!data) return <Navigate to="/" replace />;
  return (
    <PublicLayout>
      <section className="max-w-3xl mx-auto px-6 py-16 lg:py-20">
        <div className="text-xs font-semibold uppercase tracking-wider text-blue-600 mb-3">Legal</div>
        <h1 className="text-4xl font-bold text-slate-900">{data.title}</h1>
        <div className="text-sm text-slate-500 mt-2">Last updated · {data.updated}</div>
        <div className="mt-10 space-y-8">
          {data.sections.map(s => (
            <div key={s.h}>
              <h2 className="text-lg font-bold text-slate-900 mb-2">{s.h}</h2>
              <p className="text-slate-700 leading-relaxed">{s.p}</p>
            </div>
          ))}
        </div>
      </section>
    </PublicLayout>
  );
}
